import { useQuery } from '@apollo/client';
import {
  AddBlock,
  Edit,
  Ellipsis,
  FitCompany,
  SpinnerIndicators,
  Trash,
} from '@clearkit/icons';
import {
  CKActionMenu,
  CKButton,
  CKLayoutHorizontal,
  CKNavigationBar,
  CKPopover,
  CKSkeleton,
} from 'clearkit';
import { useEffect, useState } from 'react';

import { routes } from '~/components/app/appMainRoutes';
import { NavRouterItem } from '~/components/NavBar/NavRouterItem';
import { ButtonContainer } from '~/components/unified/ButtonContainer';
import { useStatusToast } from '~/components/unified/hooks';
import { trackEvent } from '~/lib/tracking';

import { Search } from '../hooks/useTargetMarket';
import TargetMarketsQuery from '../queries/TargetMarkets.graphql';
import { TargetMarketForm } from './Form';
import {
  createTargetMarket,
  destroyTargetMarket,
  TargetMarket,
  updateTargetMarket,
} from './logic';
import SaveListModal from './SaveListModal';
import { TargetMarketSidebar } from './TargetMarketSidebar';

const Sidebar = ({
  search,
  id,
  setTargetMarket,
  setSearch,
  modifiedNotSaved,
}: {
  search: Search | null;
  id: string | null;
  setTargetMarket: ({
    id,
    search,
  }: {
    id: string | null;
    search: Search | null;
  }) => void;
  setSearch: (search: Search | null) => void;
  modifiedNotSaved: boolean;
}) => {
  const { addSuccessToast, addErrorToast } = useStatusToast();
  const [initialised, setInitialised] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data, loading: dataLoading, refetch } = useQuery(TargetMarketsQuery);
  const loading = dataLoading || busy;
  const targetMarkets: TargetMarket[] =
    data?.targetMarkets?.targetMarkets || [];
  const selectedTargetMarket = id
    ? targetMarkets.find((tm) => tm.id === id)
    : undefined;

  // check URL params on initial load and set target market
  useEffect(() => {
    if (initialised) return;

    const url = window.location.href;
    const urlParams = new URL(url);
    const id = urlParams.searchParams.get('id');

    if (id && targetMarkets && !dataLoading) {
      const targetMarket = targetMarkets.find((tm) => tm.id === id);

      if (targetMarket) {
        setTargetMarket({ ...targetMarket });
      }

      setInitialised(true);
    } else if (urlParams.searchParams.get('new') === 'true') {
      setShowFilters(true);
      setInitialised(true);
    }
  }, [dataLoading]);

  const handleDiscardChanges = () => {
    if (selectedTargetMarket) {
      setTargetMarket({ ...selectedTargetMarket });
    } else {
      setShowFilters(false);
      setTargetMarket({ id: null, search: null });
    }
  };

  const handleUpsertTargetMarket = async (name?: string) => {
    if (!search) return;

    setBusy(true);

    // handle updating existing target market
    if (selectedTargetMarket) {
      const nameToUse = name || selectedTargetMarket.name;

      const succeeded = await updateTargetMarket({
        ...selectedTargetMarket,
        name: nameToUse,
        search,
      });

      if (succeeded === false) {
        addErrorToast({
          heading: 'Target Market not updated',
          description: `Sorry, there was an error updating ${nameToUse}. Please try again.`,
        });

        closeModal();

        return;
      }

      addSuccessToast({
        heading: 'Target Market updated',
        description: `You updated ${nameToUse}.`,
      });

      refetch();
      closeModal();

      return;
    }

    // if no target market selected, create a new one
    const nameToUse = name || `'Target Market #${targetMarkets.length + 1}`;

    const createdTargetMarket = await createTargetMarket({
      name: nameToUse,
      search,
    });

    if (createdTargetMarket === false) {
      addErrorToast({
        heading: 'Target Market not saved',
        description: `Sorry, there was an error saving ${nameToUse}. Please try again.`,
      });

      closeModal();
      refetch();

      return;
    }

    // Response returned id so we can redirect right away
    if (createdTargetMarket?.id) {
      addSuccessToast({
        heading: 'Target Market saved',
        description: `You saved ${nameToUse}.`,
      });

      closeModal();
      refetch();

      setTargetMarket({ ...createdTargetMarket });

      return;
    }

    // Fallback, if no target market id was returned
    const all = await refetch();
    const newTargetMarket: TargetMarket | undefined =
      all.data?.targetMarkets?.targetMarkets?.[0];

    if (!newTargetMarket) {
      addErrorToast({
        heading: 'New Target Market not created',
        description: `Sorry, there was an error creating ${nameToUse}. Please try again.`,
      });

      closeModal();

      return;
    }

    setTargetMarket({ ...newTargetMarket });

    addSuccessToast({
      heading: 'Target Market saved',
      description: `You saved ${nameToUse}.`,
    });

    closeModal();
  };

  const closeModal = () => {
    if (showSaveModal) setShowSaveModal(false);
    setBusy(false);
  };

  const handleDeleteTargetMarket = async () => {
    if (!selectedTargetMarket) return;

    setBusy(true);

    const succeeded = await destroyTargetMarket(selectedTargetMarket.id);

    if (!succeeded) {
      addErrorToast({
        heading: 'Target Market not deleted',
        description: `Sorry, there was an error deleting ${selectedTargetMarket.name}. Please try again.`,
      });

      setBusy(false);
      refetch();

      return;
    }

    addSuccessToast({
      heading: 'Target Market deleted',
      description: `You deleted ${selectedTargetMarket.name}.`,
    });

    setTargetMarket({ id: null, search: null });
    setShowFilters(false);
    setBusy(false);

    refetch();
  };

  const setupForNewTargetMarket = () => {
    setShowFilters(true);

    // add ?new= true to URL
    const url = window.location.href;
    const urlParams = new URL(url);
    urlParams.searchParams.set('new', 'true');
    window.history.pushState({}, '', urlParams.toString());

    trackEvent('target_market_new_list_clicked');
  };

  return (
    <TargetMarketSidebar
      title={selectedTargetMarket || showFilters ? 'Filters' : 'Target Markets'}
    >
      {(!!selectedTargetMarket || showFilters) && (
        <TargetMarketSidebar.Breadcrumbs
          handleReset={() => {
            setShowFilters(false);
            setTargetMarket({ id: null, search: null });
          }}
        >
          {loading ? (
            <CKSkeleton className="w-full h-3 shrink-1 grow" />
          ) : (
            <span title={selectedTargetMarket?.name || ''}>
              {selectedTargetMarket?.name || 'New Target Market'}
            </span>
          )}
        </TargetMarketSidebar.Breadcrumbs>
      )}

      {loading ? (
        <div className="flex items-center justify-center p-6">
          <SpinnerIndicators />
        </div>
      ) : selectedTargetMarket || showFilters ? (
        <TargetMarketForm search={search || {}} setSearch={setSearch} />
      ) : targetMarkets && targetMarkets.length ? (
        <div>
          <p className="text-sm font-medium px-[26px]">Saved Target Markets</p>

          <CKNavigationBar
            className="pt-0 bg-transparent border-r-0 !w-full"
            isExpanded
            isResponsive={false}
          >
            <CKNavigationBar.Section className="space-y-1">
              {targetMarkets.map((targetMarket) => (
                <button
                  className="w-full text-left"
                  key={targetMarket.id}
                  onClick={() => setTargetMarket({ ...targetMarket })}
                >
                  <NavRouterItem
                    className="!font-normal !w-full"
                    key={targetMarket.id}
                    label={targetMarket.name}
                    to={`${routes.targetMarkets}?id=${targetMarket.id}`}
                  >
                    <CKNavigationBar.ItemLeft>
                      <FitCompany />
                    </CKNavigationBar.ItemLeft>
                  </NavRouterItem>
                </button>
              ))}
            </CKNavigationBar.Section>
          </CKNavigationBar>
        </div>
      ) : (
        <div className="flex flex-col items-center p-6 mx-6 text-center ck-box ck-box--well gap-2">
          <h3 className="font-semibold">Welcome to Target Markets</h3>

          <p className="text-sm">
            Build a list of target companies using attributes such as industry,
            number of employees, technology used, and more.
          </p>

          <CKButton
            leftIcon={<AddBlock />}
            onClick={setupForNewTargetMarket}
            variant="bold"
            variantColor="blue"
          >
            New list
          </CKButton>
        </div>
      )}

      <TargetMarketSidebar.Footer>
        <CKLayoutHorizontal>
          {selectedTargetMarket ? (
            <>
              <ButtonContainer>
                <CKButton
                  isDisabled={!modifiedNotSaved}
                  onClick={() => handleUpsertTargetMarket()}
                  variant="bold"
                  variantColor="blue"
                >
                  Update list
                </CKButton>

                {modifiedNotSaved && (
                  <CKButton onClick={handleDiscardChanges}>Undo</CKButton>
                )}
              </ButtonContainer>

              <CKPopover
                popover={
                  <CKActionMenu>
                    <CKActionMenu.Item
                      icon={<Edit />}
                      onClick={() => setShowSaveModal(true)}
                    >
                      Rename list
                    </CKActionMenu.Item>

                    <CKActionMenu.Item
                      icon={<Trash />}
                      isDestructive
                      onClick={handleDeleteTargetMarket}
                    >
                      Delete list
                    </CKActionMenu.Item>
                  </CKActionMenu>
                }
                popoverPlacement="top-end"
              >
                <CKButton>
                  <Ellipsis />
                </CKButton>
              </CKPopover>
            </>
          ) : showFilters ? (
            <CKButton
              isDisabled={!modifiedNotSaved || !search}
              onClick={() => setShowSaveModal(true)}
              variant="bold"
              variantColor="blue"
            >
              Save list
            </CKButton>
          ) : (
            <CKButton
              leftIcon={<AddBlock />}
              onClick={setupForNewTargetMarket}
              variant="bold"
              variantColor="blue"
            >
              New list
            </CKButton>
          )}
        </CKLayoutHorizontal>
      </TargetMarketSidebar.Footer>

      <SaveListModal
        handleCancel={() => {
          setShowSaveModal(false);
          setBusy(false);
        }}
        handleSave={async (name: string) => {
          if (!search) return;

          await handleUpsertTargetMarket(name);
        }}
        isOpen={showSaveModal}
        loading={loading}
        selected={selectedTargetMarket}
      />
    </TargetMarketSidebar>
  );
};

export { Sidebar };
